import { formatTemplateFieldValue } from '@/utils';
import { TemplateFieldTypeEnum, TemplateFilterInput, AssemblyTemplateQuery } from '@/types';
import type {
  UnitSystemEnum,
  AllDecksForFilterSideBarQuery,
  AllPaintRegionsForFilterAndSearchPanelQuery,
  AllWorkpacksForFilterSideBarQuery,
} from '@/__generated__/graphql';
import type { FilterField, FilterPill, OptionItem } from './InsightsSearchBar.types';
import {
  MAX_VISIBLE_ITEMS,
  SPATIAL_DECKS,
  SPATIAL_FIELD_NAMES,
  SPATIAL_PAINT,
  SPATIAL_WORKPACK,
} from './constants';

type TemplateField = NonNullable<AssemblyTemplateQuery['fields']>[number];
type DeckItem = NonNullable<AllDecksForFilterSideBarQuery['allDecks']>[number];
type PaintRegionItem = NonNullable<
  AllPaintRegionsForFilterAndSearchPanelQuery['allPaintRegions']
>[number];
type WorkpackItem = NonNullable<AllWorkpacksForFilterSideBarQuery['allWorkpacks']>[number];

interface BuildFilterPillsArgs {
  selectedTemplateFilters: TemplateFilterInput[];
  selectedDeckIds: string[];
  selectedPaintRegionIds: string[];
  selectedWorkpackIds: string[];
  assemblyTemplate: AssemblyTemplateQuery | undefined;
  decksData: AllDecksForFilterSideBarQuery | undefined;
  paintRegionsData: AllPaintRegionsForFilterAndSearchPanelQuery | undefined;
  workpacksData: AllWorkpacksForFilterSideBarQuery | undefined;
  unitSystem: UnitSystemEnum;
  structurePipes: Record<string, number> | null | undefined;
}

export function buildFilterPills({
  selectedTemplateFilters,
  selectedDeckIds,
  selectedPaintRegionIds,
  selectedWorkpackIds,
  assemblyTemplate,
  decksData,
  paintRegionsData,
  workpacksData,
  unitSystem,
  structurePipes,
}: BuildFilterPillsArgs): FilterPill[] {
  const pills: FilterPill[] = [];

  // Template filters
  for (const filter of selectedTemplateFilters) {
    const field = assemblyTemplate?.fields?.find(
      (f: TemplateField) => (f.key ?? f.id) === filter.id
    );
    const fieldName = field?.name ?? filter.id;

    for (const val of filter.value) {
      const displayValue = formatTemplateFieldValue({
        value: val,
        templateFieldKey: field?.key ?? '',
        unit: field?.unit,
        displayUnit: field?.displayUnit,
        unitSystem,
        structurePipes,
      });
      pills.push({
        fieldName,
        fieldKey: filter.id,
        value: val,
        displayValue,
        type: 'template',
      });
    }
  }

  // Deck filters
  for (const id of selectedDeckIds) {
    const deck = decksData?.allDecks?.find((d: DeckItem) => d.id === id);
    if (deck) {
      pills.push({
        fieldName: SPATIAL_FIELD_NAMES[SPATIAL_DECKS],
        fieldKey: SPATIAL_DECKS,
        value: id,
        displayValue: deck.name,
        type: 'deck',
      });
    }
  }

  // Paint region filters
  for (const id of selectedPaintRegionIds) {
    const paintRegion = paintRegionsData?.allPaintRegions?.find(
      (p: PaintRegionItem) => p.id === id
    );
    if (paintRegion) {
      pills.push({
        fieldName: SPATIAL_FIELD_NAMES[SPATIAL_PAINT],
        fieldKey: SPATIAL_PAINT,
        value: id,
        displayValue: paintRegion.name,
        type: 'paintRegion',
      });
    }
  }

  // Workpack filters
  for (const id of selectedWorkpackIds) {
    const workpack = workpacksData?.allWorkpacks?.find((w: WorkpackItem) => w.id === id);
    if (workpack) {
      pills.push({
        fieldName: SPATIAL_FIELD_NAMES[SPATIAL_WORKPACK],
        fieldKey: SPATIAL_WORKPACK,
        value: id,
        displayValue: workpack.name,
        type: 'workpack',
      });
    }
  }

  return pills;
}

interface BuildFilterFieldsArgs {
  assemblyTemplate: AssemblyTemplateQuery | undefined;
  decksData: AllDecksForFilterSideBarQuery | undefined;
  paintRegionsData: AllPaintRegionsForFilterAndSearchPanelQuery | undefined;
  workpacksData: AllWorkpacksForFilterSideBarQuery | undefined;
  unitSystem: UnitSystemEnum;
  structurePipes: Record<string, number> | null | undefined;
}

export function buildFilterFields({
  assemblyTemplate,
  decksData,
  paintRegionsData,
  workpacksData,
  unitSystem,
  structurePipes,
}: BuildFilterFieldsArgs): FilterField[] {
  const fields: FilterField[] = [];

  // Template fields - only include queryable fields
  const templateFields = assemblyTemplate?.fields ?? [];

  for (const field of templateFields) {
    const rawOptions: string[] = [];

    if (field.options?.length) {
      rawOptions.push(...field.options);
    }

    // Handle optionsData if present
    const optionsData = (field as TemplateField & { optionsData?: { value: string }[] })
      .optionsData;
    if (optionsData?.length) {
      rawOptions.push(...optionsData.map((d) => d.value).filter(Boolean));
    }

    const optionItems: OptionItem[] = rawOptions.slice(0, MAX_VISIBLE_ITEMS).map((opt) => ({
      id: opt,
      display: formatTemplateFieldValue({
        value: opt,
        templateFieldKey: field.key ?? '',
        unit: field.unit,
        displayUnit: field.displayUnit,
        unitSystem,
        structurePipes,
      }),
    }));

    fields.push({
      id: field.id,
      name: field.name,
      type: 'template',
      templateField: field,
      options: optionItems,
    });
  }

  // Spatial fields
  fields.push({
    id: SPATIAL_DECKS,
    name: SPATIAL_FIELD_NAMES[SPATIAL_DECKS],
    type: 'spatial',
    options:
      decksData?.allDecks?.map((d: DeckItem) => ({
        id: d.id,
        display: d.name,
      })) ?? [],
  });

  fields.push({
    id: SPATIAL_PAINT,
    name: SPATIAL_FIELD_NAMES[SPATIAL_PAINT],
    type: 'spatial',
    options:
      paintRegionsData?.allPaintRegions?.map((p: PaintRegionItem) => ({
        id: p.id,
        display: p.name,
      })) ?? [],
  });

  fields.push({
    id: SPATIAL_WORKPACK,
    name: SPATIAL_FIELD_NAMES[SPATIAL_WORKPACK],
    type: 'spatial',
    options:
      workpacksData?.allWorkpacks?.map((w: WorkpackItem) => ({
        id: w.id,
        display: w.name,
      })) ?? [],
  });

  return fields;
}

export function filterFieldsBySearchTerm(fields: FilterField[], searchTerm: string): FilterField[] {
  if (!searchTerm) {
    return fields.slice(0, MAX_VISIBLE_ITEMS);
  }

  const searchLower = searchTerm.toLowerCase();
  return fields
    .filter((field) => field.name.toLowerCase().includes(searchLower))
    .slice(0, MAX_VISIBLE_ITEMS);
}

export function filterOptionsBySearchTerm(options: OptionItem[], searchTerm: string): OptionItem[] {
  if (!searchTerm) {
    return options.slice(0, MAX_VISIBLE_ITEMS);
  }

  const searchLower = searchTerm.toLowerCase();
  return options
    .filter(
      (opt) =>
        opt.display?.toLowerCase().includes(searchLower) ||
        opt.id?.toLowerCase().includes(searchLower)
    )
    .slice(0, MAX_VISIBLE_ITEMS);
}
